import { ServerTable } from '@civilgrid/ui/components';
import { buildSearchPayload, useTableStoreState } from '@civilgrid/ui/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { listFilteredDataRequests } from '~/api/civilsearch';
import { dataRequestColumns } from './dataRequestColumns';
import { getAllUtilityTypes, getElectric, getGas, getSewer, getStorm, getWater } from './dataRequestFilterShortcuts';

export default function DataRequestsTable({ projectId }: { projectId: string }) {
  const stateControl = useTableStoreState({
    defaultPageSize: 100,
    initialSorting: [{ id: 'agency.agencyName', desc: false }],
    initialFilters: getAllUtilityTypes(),
  });

  useEffect(() => {
    stateControl.setCurrentPage(1);
  }, [stateControl.setCurrentPage]);

  const { pagination, sorting, filters, globalFilter } = stateControl;

  const query = useQuery({
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryKey: ['project-utility-check-data-requests', projectId, { pagination, sorting, filters, globalFilter }],
    queryFn: async () => {
      const searchPayload = buildSearchPayload({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        filters,
        globalFilter,
      });

      // The backend OR's top-level filter array elements, so we combine
      // everything into a single AND group. User filter groups are wrapped
      // in arrays so the backend's getFilter recurses via filtersToSql.
      const userFilterGroups = searchPayload.filters ?? [];
      const projectFilter = {
        property: 'project.id',
        operator: 'eq',
        value: projectId,
      };

      const apiPayload = {
        ...searchPayload,
        sort: searchPayload.sort?.[0],
        filters: [
          {
            filterType: 'and' as const,
            filters: [projectFilter, ...userFilterGroups.map((g) => [g])],
          },
        ],
      };

      return listFilteredDataRequests({ search: apiPayload });
    },
  });

  const shortcuts = [
    { label: 'SSWEG', getFilter: getAllUtilityTypes },
    { label: 'Storm', getFilter: getStorm },
    { label: 'Sewer', getFilter: getSewer },
    { label: 'Water', getFilter: getWater },
    { label: 'Electric', getFilter: getElectric },
    { label: 'Gas', getFilter: getGas },
  ];

  return (
    <ServerTable
      id="project-utility-check-data-requests"
      aria-label="Data requests"
      columns={dataRequestColumns}
      filterShortcuts={shortcuts}
      data={{
        data: query.data?.requests ?? [],
        rowCount: query.data?.totalDocuments ?? 0,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error || null,
      }}
      stateControl={stateControl}
      enableRowSelection={false}
      enableColumnGrouping={false}
      isLoading={query.isLoading}
      className="h-full"
    />
  );
}
